import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode | React.ComponentType<{ error?: Error; resetError: () => void }>;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo.componentStack);
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        // Check if fallback is a component type or JSX element
        if (typeof this.props.fallback === 'function') {
          const FallbackComponent = this.props.fallback;
          return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
        } else {
          // It's a JSX element, return it directly
        return this.props.fallback;
        }
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
          <div className="max-w-md w-full bg-white shadow-[0_4px_12px_rgba(0,0,0,0.08)] rounded-lg p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-[#171717] mb-2">
                Something went wrong
              </h3>
              <p className="text-sm text-[#888] mb-4">
                An unexpected error occurred. We are already working on fixing it.
              </p>
              <div className="space-y-2">
                <button
                  onClick={this.resetError}
                  className="w-full bg-[#171717] text-white px-4 py-2 rounded-lg hover:bg-[#333] transition-colors"
                >
                  Try again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full bg-[#171717] text-white px-4 py-2 rounded-lg hover:bg-[#444] transition-colors"
                >
                  Refresh page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
} 

// Export both default and named export
export { ErrorBoundary };
export default ErrorBoundary; 